import {geohashForLocation} from 'geofire-common';
import {MapService} from '@/services/MapService';
import {FEATURES_COLLECTION, OVERLAY_COLLECTION} from '@/lib/constants';
import {firestoreCollectionByCollectionName} from '@/internal/store/collections';
import {
  MIN_OVERLAY_VISIBILITY_ZOOM,
  extractFieldSelectionsFromSettings,
  filterDisplayFields,
  filterExcludedFields,
  formatCompDisplayFieldsForExport,
  normalizeIconForExport,
  updateDisplayFields,
} from './exportDisplayFieldRules';
import type {
  ExportComp,
  ExportDisplayField,
  ExportFieldSettings,
  FieldSelections,
  FormattedMarkerContent,
} from './exportDisplayFieldRules';

/**
 * Formats features, overlays, and markers into the document shape consumed by shared (external) maps.
 */

interface ExportGraphic {
  toJSON(): {bounds?: unknown; graphicData?: unknown};
}

interface SmapsFeature {
  id: string;
  featureType: string;
  icon?: {name: string};
  geohash?: string;
  name?: string;
  displayFields: ExportDisplayField[];
  comps?: ExportComp[];
  graphic: ExportGraphic | Promise<ExportGraphic>;
  parent: {id: string; folders: unknown[]};
  originalFeatureMetadata?: unknown;
}

interface OverlayFeature {
  id: string;
  featureType: string;
  geohash?: string;
  name?: string;
  displayFields: ExportDisplayField[];
  comps?: ExportComp[];
  graphic: ExportGraphic | Promise<ExportGraphic>;
}

interface ExportOverlay {
  id: string;
  name?: string;
  visible?: boolean;
  isTaxOverlay?: boolean;
  features: {values(): Iterable<OverlayFeature>};
  folders?: unknown[];
}

interface ExportMarker {
  markerData: {id: string; name?: string; icon: string};
  parent?: {id: string};
  [key: string]: unknown;
}

type FormatFn = (marker: unknown, id?: string) => Promise<FormattedMarkerContent> | FormattedMarkerContent;

interface ExportedFeature {
  id: string;
  bounds: unknown;
  geohash?: string;
  name?: string;
  displayFields: ExportDisplayField[];
  graphicData: unknown;
  fieldSelections: FieldSelections;
  comps?: ExportComp[];
  originalFeatureMetadata?: unknown;
}

/**
 * Given an array of Features and export settings, formats the Features for export.
 */
const formatFeatures = async (
  features: SmapsFeature[],
  exportFieldSettings: ExportFieldSettings,
): Promise<[ExportedFeature[], unknown[]]> => {
  const allFeatures: ExportedFeature[] = [];
  const featureFolders: unknown[] = [];

  const parentFoldersProcessed = new Set<string>();
  for (const feature of features) {
    const graphic = await feature.graphic;
    const json = graphic.toJSON();

    let lookup = feature.featureType;
    if (feature.featureType.startsWith('blockPlan')) {
      lookup = 'blockPlan';
    } else if (feature.featureType === 'placeMarker') {
      lookup = feature.icon!.name;
    }

    let displayFields = feature.displayFields;

    // Keep track of which field selections were made for this feature so that the feature data can be updated later
    let fieldSelections: FieldSelections;

    const featureExportSettings = exportFieldSettings.get(lookup);
    if (featureExportSettings) {
      displayFields = filterExcludedFields(displayFields, featureExportSettings);
      fieldSelections = extractFieldSelectionsFromSettings(featureExportSettings);
    } else {
      fieldSelections = {displayFieldLabels: displayFields.map((field) => field.label)};
    }

    const featureObj: ExportedFeature = {
      id: feature.id,
      bounds: json.bounds,
      geohash: feature.geohash,
      name: feature.name || '',
      displayFields,
      graphicData: json.graphicData,
      fieldSelections,
    };

    // Filter and format comps based on user selections
    if (feature?.comps?.length) {
      const comps = feature.comps.map((comp) => {
        const {excluded, displayFields: compDisplayFields} =
          formatCompDisplayFieldsForExport(comp, featureExportSettings);
        if (excluded) return null;

        return {
          ...comp,
          link: null,
          parent: null,
          name: comp.name,
          displayFields: compDisplayFields,
          type: comp.type,
        };
      }).filter((comp) => comp !== null);

      if (comps?.length > 0) {
        featureObj.comps = comps as ExportComp[];
      }
    }

    if (feature.originalFeatureMetadata) {
      featureObj.originalFeatureMetadata = feature.originalFeatureMetadata;
    }

    allFeatures.push(featureObj);

    if (!parentFoldersProcessed.has(feature.parent.id)) {
      featureFolders.push(...feature.parent.folders);
      parentFoldersProcessed.add(feature.parent.id);
    }
  }

  return [allFeatures, featureFolders];
};

/**
 * Given an array of Overlays, returns an array of Feature data ready for export.
 */
const formatOverlayFeatures = async (
  overlayCollection: ExportOverlay[],
  exportFieldSettings: ExportFieldSettings,
): Promise<[ExportedFeature[], unknown[]]> => {
  const allFeatures: ExportedFeature[] = [];
  const overlayFolders: unknown[] = [];

  // For every overlay in the overlays collection...
  for (const overlay of overlayCollection) {
    // If the overlay isn't permitted to be visible or the map is zoomed out too far, skip it
    if (!overlay.visible || MapService.getMap()!.getZoom()! < MIN_OVERLAY_VISIBILITY_ZOOM || overlay.isTaxOverlay) {
      console.warn(`Overlay ${overlay.name} is not visible and will not be included in the shared map.`);
      continue;
    }

    // Add the features to the parent features array
    // Some feature fields should not be included in JSON
    for (const feature of overlay.features.values()) {
      const graphic = await feature.graphic;
      const json = graphic.toJSON();

      let lookup = feature.featureType;
      if (feature.featureType.startsWith('blockPlan')) {
        lookup = 'blockPlan';
      }

      let displayFields = feature.displayFields;

      // Keep track of which field selections were made for this feature so that the feature data can be updated later
      let fieldSelections: FieldSelections;

      // Filter out display fields that are not selected for export
      const featureExportSettings = exportFieldSettings.get(lookup);
      if (featureExportSettings) {
        displayFields = filterExcludedFields(displayFields, featureExportSettings);
        fieldSelections = extractFieldSelectionsFromSettings(featureExportSettings);
      } else {
        fieldSelections = {displayFieldLabels: displayFields.map((field) => field.label)};
      }

      // Remove the parent field from marker comps to allow stringifying
      const comps = feature.comps?.map((comp) => {
        const {excluded, displayFields: compDisplayFields} = formatCompDisplayFieldsForExport(
          comp,
          featureExportSettings,
          {excludeWhenCategoryMissing: true},
        );
        if (excluded) return null;

        return {
          ...comp,
          link: null,
          parent: null,
          name: comp.name,
          displayFields: compDisplayFields,
          type: comp.type,
        };
      }).filter((comp) => comp !== null);

      const formatted: ExportedFeature = {
        id: feature.id,
        bounds: json.bounds,
        geohash: feature.geohash,
        name: feature.name || '',
        displayFields,
        graphicData: json.graphicData,
        originalFeatureMetadata: {
          collection: OVERLAY_COLLECTION,
          docId: overlay.id,
          tabId: 'overlays',
          subcollection: FEATURES_COLLECTION,
          featureId: feature.id,
        },
        fieldSelections,
      };
      if (comps?.some((comp) => comp)) {
        formatted.comps = comps as ExportComp[];
      }
      allFeatures.push(formatted);
    }

    // Make all folders children of a parent folder for the overlay
    // This will preserve top level features and folders ordering.
    const random = Math.random().toString();
    const uid = random + Date.now();
    overlayFolders.push({
      children: overlay.folders ?? null,
      name: overlay.name ?? 'Untitled Overlay',
      folderID: uid,
      showBody: true,
      visible: true,
      defaultVisibility: true,
    });
  }

  return [allFeatures, overlayFolders];
};

/**
 * Given a collection of markers, returns an array of objects ready to be saved as placemarker graphics.
 */
const formatMarkerFeatures = async (
  markerCollection: ExportMarker[],
  collectionName: string,
  formatFn: FormatFn,
  exportFieldSettings: ExportFieldSettings,
): Promise<[ExportedFeature[], unknown[]]> => {
  const allFeatures: ExportedFeature[] = [];
  const markerFolders: unknown[] = [];

  // To make markers a feature, we need icon, title, position, displayFields, and comps.
  // Converting from raw to info window content provides all of these fields.
  const markerPromises = markerCollection.map(async (marker) => {
    // Get the info window content for easy access to location and display fields
    const content = await formatFn(marker, marker?.markerData?.id);
    const lat = content.location.lat();
    const lng = content.location.lng();
    const bounds = {
      north: lat,
      south: lat,
      east: lng,
      west: lng,
    };
    const geohash = geohashForLocation([lat, lng]);

    // Filter and update the display fields
    let filteredDisplayFields = filterDisplayFields(content.displayFields)!;

    // Filter out display fields that are not selected for export
    let markerExportSettings = exportFieldSettings.get(collectionName);
    // For comp mode, combine the parent and comp settings
    let compType: string | undefined;
    if (collectionName === 'competitiveBuildingComps') {
      const parentSettings = exportFieldSettings.get('competitiveBuildings') ?? [];
      compType = filteredDisplayFields.find((field) => field.label === 'Comp Type')?.value as string | undefined;
      const compSettings = exportFieldSettings.get(compType as string) ?? [];
      markerExportSettings = [...parentSettings, ...compSettings];
    }

    // Keep track of which field selections were made for this feature so that the feature data can be updated later
    let fieldSelections: FieldSelections;

    if (markerExportSettings) {
      filteredDisplayFields = filterExcludedFields(filteredDisplayFields, markerExportSettings);
      fieldSelections = extractFieldSelectionsFromSettings(markerExportSettings);
    } else {
      fieldSelections = {
        displayFieldLabels: filteredDisplayFields.map((field) => field.label),
      };
    }

    let displayFields: ExportDisplayField[] = [];
    if (filteredDisplayFields?.length) {
      displayFields = updateDisplayFields(filteredDisplayFields);
    }

    // Remove the parent field from marker comps to allow stringifying
    const comps = content.comps?.map((comp) => {
      // Remove header links from the comps
      if (comp.link) {
        comp.link = null;
      }

      const {excluded, displayFields: compDisplayFields} =
        formatCompDisplayFieldsForExport(comp, exportFieldSettings.get(collectionName));
      if (excluded) return null;

      delete comp.parent;

      return {
        ...comp,
        name: comp.name,
        displayFields: compDisplayFields,
        type: comp.type,
      };
    });

    // Filter out comps that are not selected for export
    const filteredComps = comps?.filter((comp) => comp !== null);

    const exportIcon = normalizeIconForExport(marker.markerData.icon);

    const originalFeatureMetadata: {
      collection: string; docId: string; tabId: string | null; icon: string; compId?: string;
    } = {
      collection: firestoreCollectionByCollectionName[collectionName],
      docId: marker.markerData.id,
      tabId: collectionName,
      icon: exportIcon,
    };

    // For comp view mode, adjust the original feature metadata to point to the property document and comp type
    if (collectionName === 'competitiveBuildingComps') {
      originalFeatureMetadata.collection = 'beyondMaps-properties';
      originalFeatureMetadata.docId = marker.parent!.id;
      originalFeatureMetadata.tabId = compType || null;
      originalFeatureMetadata.compId = marker.markerData.id;
    }

    // Return an object ready to be saved as a placemarker graphic
    return {
      id: marker.markerData.id,
      bounds,
      comps: filteredComps as ExportComp[] | undefined,
      displayFields,
      geohash,
      graphicData: {
        bounds,
        featureType: 'placeMarker',
        icon: exportIcon,
        id: marker.markerData.id,
        title: content.title,
      },
      name: marker.markerData.name,
      originalFeatureMetadata,
      fieldSelections,
    };
  });
  const markers = await Promise.all(markerPromises);
  allFeatures.push(...markers);

  // Push the marker ids to the pseudo overlay folders array
  const markerIds = markers.map((marker) => ({
    id: marker.graphicData.id,
  }));
  markerFolders.push(...markerIds);

  return [allFeatures, markerFolders];
};

export {
  formatFeatures,
  formatOverlayFeatures,
  formatMarkerFeatures,
};
